import Icon from "@react-native-vector-icons/material-design-icons";
import theme from "assets/theme";
import React, { useCallback, useContext, useEffect, useState } from "react";
import { StyleSheet, Text, View } from "react-native";
import CustomModal from "webComponentsFormElements/CustomModal.component";
import PressableContainer from "webComponentsFormElements/PressableContainer.component";
import SearchBar from "webComponentsFormElements/inputs/SearchBar.component";
import { OrganizationContext } from "webContext/Organization.context";

const classes = StyleSheet.create({
  root: {
    gap: 10,
    width: "400px",
  },
  labelText: {
    fontSize: 18,
    color: theme.color.backgroundText,
  },
  bodyText: {
    fontSize: 14,
    color: theme.color.backgroundText,
    textAlign: "center",
  },
  labelTextDisabled: {
    color: theme.color.disabled,
  },
  labelTextBold: {
    fontWeight: theme.font.weight.semibold,
  },
  row: {
    flexDirection: "row",
    alignItems: "center",
    paddingHorizontal: 4,
    paddingVertical: 8,
    justifyContent: "space-between",
    backgroundColor: theme.color.background,
  },
  columnsContainer: {
    flexDirection: "row",
    gap: 20,
  },
  columnContainer: {
    flex: 1,
  },
});

export default function DependentSelectionModal(props) {
  const {
    open,
    onClose,
    isOrganizationDependent,
    setIsOrganizationDependent,
    dependentTeamIds,
    setDependentTeamIds,
    dependentLocationIds,
    setDependentLocationIds,
    canSave,
    setCanSave,
  } = props;

  const { organizationColor, teamList, locationList } =
    useContext(OrganizationContext);

  const [searchText, setSearchText] = useState("");

  const [newIsOrganizationDependent, setNewIsOrganizationDependent] = useState(
    isOrganizationDependent,
  );
  const [newDependentTeamIds, setNewDependentTeamIds] = useState(new Set());
  const [newDependentLocationIds, setNewDependentLocationIds] = useState(
    new Set(),
  );

  useEffect(() => {
    setNewIsOrganizationDependent(isOrganizationDependent);
    setNewDependentTeamIds(new Set([...dependentTeamIds]));
    setNewDependentLocationIds(new Set([...dependentLocationIds]));
  }, [isOrganizationDependent, dependentTeamIds, dependentLocationIds]);

  const onSave = useCallback(() => {
    setIsOrganizationDependent(newIsOrganizationDependent);
    setDependentTeamIds(newDependentTeamIds);
    setDependentLocationIds(newDependentLocationIds);
    if (!canSave) {
      const hasChanges =
        newIsOrganizationDependent !== isOrganizationDependent ||
        newDependentTeamIds.symmetricDifference(dependentTeamIds).size !== 0 ||
        newDependentLocationIds.symmetricDifference(dependentLocationIds)
          .size !== 0;
      setCanSave(hasChanges);
    }
    onClose();
  }, [
    setIsOrganizationDependent,
    newIsOrganizationDependent,
    setDependentTeamIds,
    newDependentTeamIds,
    setDependentLocationIds,
    newDependentLocationIds,
    onClose,
    canSave,
    dependentLocationIds,
    dependentTeamIds,
    isOrganizationDependent,
    setCanSave,
  ]);

  const toggleTeam = useCallback(
    (teamId) => {
      if (newDependentTeamIds.has(teamId)) {
        newDependentTeamIds.delete(teamId);
      } else {
        newDependentTeamIds.add(teamId);
      }

      setNewDependentTeamIds(new Set(newDependentTeamIds));
    },
    [newDependentTeamIds],
  );

  const toggleLocation = useCallback(
    (locationId) => {
      if (newDependentLocationIds.has(locationId)) {
        newDependentLocationIds.delete(locationId);
      } else {
        newDependentLocationIds.add(locationId);
      }

      setNewDependentLocationIds(new Set(newDependentLocationIds));
    },
    [newDependentLocationIds],
  );

  const toggleEntireOrganization = useCallback(() => {
    setNewIsOrganizationDependent((wasDependent) => {
      if (!wasDependent) {
        setNewDependentTeamIds(new Set());
        setNewDependentLocationIds(new Set());
      }

      return !wasDependent;
    });
  }, []);

  return (
    <CustomModal open={open} onClose={onSave} title="Select Dependents" scroll>
      <View style={classes.root}>
        <PressableContainer
          style={classes.row}
          onPress={toggleEntireOrganization}
        >
          <Text style={[classes.labelText, classes.labelTextBold]}>
            Entire Organization
          </Text>
          <Icon
            name={
              newIsOrganizationDependent ? "check" : "checkbox-blank-outline"
            }
            size={25}
            color={organizationColor}
          />
        </PressableContainer>

        <SearchBar value={searchText} onChangeText={setSearchText} />
        {newIsOrganizationDependent && (
          <Text style={classes.bodyText}>
            To select individual teams and locations deselect&nbsp;
            <i>Entire Organization</i>.
          </Text>
        )}
        <View style={classes.columnsContainer}>
          <View style={classes.columnContainer}>
            <Text style={[classes.labelText, classes.labelTextBold]}>
              Teams
            </Text>
            {teamList
              .filter((team) =>
                team.name.toLowerCase().includes(searchText.toLowerCase()),
              )
              .map((team) => (
                <PressableContainer
                  key={team.teamId}
                  style={classes.row}
                  onPress={() =>
                    !newIsOrganizationDependent && toggleTeam(team.teamId)
                  }
                  disabled={newIsOrganizationDependent}
                >
                  <Text
                    style={[
                      classes.labelText,
                      newIsOrganizationDependent && classes.labelTextDisabled,
                    ]}
                  >
                    {team.name}
                  </Text>
                  <Icon
                    name={
                      newDependentTeamIds.has(team.teamId) ||
                      newIsOrganizationDependent
                        ? "check"
                        : "checkbox-blank-outline"
                    }
                    size={25}
                    color={
                      newIsOrganizationDependent
                        ? theme.color.disabled
                        : organizationColor
                    }
                  />
                </PressableContainer>
              ))}
          </View>

          <View style={classes.columnContainer}>
            <Text style={[classes.labelText, classes.labelTextBold]}>
              Locations
            </Text>
            {locationList
              .filter((location) =>
                location.name.toLowerCase().includes(searchText.toLowerCase()),
              )
              .map((location) => (
                <PressableContainer
                  key={location.locationId}
                  style={classes.row}
                  onPress={() =>
                    !newIsOrganizationDependent &&
                    toggleLocation(location.locationId)
                  }
                  disabled={newIsOrganizationDependent}
                >
                  <Text
                    style={[
                      classes.labelText,
                      newIsOrganizationDependent && classes.labelTextDisabled,
                    ]}
                  >
                    {location.name}
                  </Text>
                  <Icon
                    name={
                      newDependentLocationIds.has(location.locationId) ||
                      newIsOrganizationDependent
                        ? "check"
                        : "checkbox-blank-outline"
                    }
                    size={25}
                    color={
                      newIsOrganizationDependent
                        ? theme.color.disabled
                        : organizationColor
                    }
                  />
                </PressableContainer>
              ))}
          </View>
        </View>
      </View>
    </CustomModal>
  );
}
